import { useState } from "react";
import type { Observation } from "@/data/queries";
import { useMediaUrl } from "@/data/media";
import { KIND_META, MediaThumb, obsKind, TypePill } from "./visit";
import { clock } from "./format";

// Observation card (preview/components-observation-card.html): variant header
// strip with time + type + sync chip, white body.
export function ObservationCard({ o, index, onOpenPhoto }: { o: Observation; index: number; onOpenPhoto?: (o: Observation) => void }) {
  const kind = obsKind(o);
  const [expanded, setExpanded] = useState(false);
  const text = kind === "text" ? o.body : kind === "audio" ? o.raw_transcript || o.whatsapp_message_content_ai : o.image_text_ai || o.whatsapp_message_content_ai || o.body;
  const pending = !o.processed;
  return (
    <article className={`obs obs--${kind}`} id={`obs-${o.id}`}>
      <header className="obs-head">
        <span className="obs-time">{clock(o.created_at)}</span>
        <TypePill kind={kind} />
        {o.location_label && <span className="hint">📍 {o.location_label}</span>}
        <span className={`obs-chip ${pending ? "obs-chip--pending" : "obs-chip--synced"}`}>{pending ? "◌ PROCESSING" : "✓ SYNCED"}</span>
      </header>
      <div className="obs-body">
        {kind === "photo" && (
          <button className="obs-photo" onClick={() => onOpenPhoto?.(o)} aria-label="Open photo">
            <MediaThumb o={o} alt={index} />
          </button>
        )}
        {kind === "audio" && <AudioLine o={o} />}
        {text ? (
          <p className={`obs-text ${expanded ? "" : "clamp-3"}`} onClick={() => setExpanded((x) => !x)}>
            {kind === "audio" && <span className="obs-label">Transcript · </span>}
            {kind === "photo" && <span className="obs-label">AI read · </span>}
            {text}
          </p>
        ) : (
          <p className="obs-text subtle">{pending ? `${KIND_META[kind].label} received. Processing.` : "No text."}</p>
        )}
      </div>
    </article>
  );
}

function AudioLine({ o }: { o: Observation }) {
  const url = useMediaUrl(o.storage_key);
  // Deterministic faux-waveform from the id: decoration only, aria-hidden.
  const bars = Array.from({ length: 48 }, (_, i) => 20 + ((o.id.charCodeAt(i % o.id.length) * (i + 7)) % 70));
  return (
    <div className="obs-audio">
      {url ? <audio controls preload="none" src={url} /> : (
        <div className="wave" aria-hidden="true">{bars.map((h, i) => <i key={i} style={{ height: `${h}%` }} />)}</div>
      )}
    </div>
  );
}
